import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Typography,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import TechnicianForm from '../components/technicians/TechnicianForm';
import TechniciansList from '../components/technicians/TechniciansList';
import PageTitle from '../components/layout/PageTitle';
import { useTechnicians } from '../contexts/TechniciansContext';
import { useSkills } from '../contexts/SkillsContext';
import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';

const TechniciansPage = () => {
  const { deleteTechnician } = useTechnicians();
  const { selectedTeam } = useTeam();
  const { currentUser } = useAuth();
  const { skills } = useSkills();
  const [openForm, setOpenForm] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    technician: null
  });
  const [refreshKey, setRefreshKey] = useState(0);

  // Vérifier les permissions pour l'équipe sélectionnée
  const hasPermission = (action) => {
    console.log('🔍 Vérification permissions:', { currentUser: currentUser?.email, selectedTeam: selectedTeam?.name, action });
    if (!currentUser || !selectedTeam) return false;
    
    // SuperAdmin peut tout faire
    if (currentUser.role === 'SUPER_ADMIN') return true;
    
    // Admin ou adjoint de l'équipe peut tout faire
    if (selectedTeam.adminUserId === currentUser.id || 
        selectedTeam.assistantAdminUserId === currentUser.id) {
      return true;
    }
    
    // Vérifier les permissions de délégation
    const permissions = selectedTeam.settings?.permissions?.technicians || 
                       { view: true, add: false, edit: false, delete: false };
    
    return permissions[action] || false;
  };

  const handleOpenForm = (technician = null) => {
    setSelectedTechnician(technician);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedTechnician(null);
  };

  const handleDelete = (technician) => {
    setDeleteDialog({
      open: true,
      technician: technician
    });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.technician) {
      try {
        await deleteTechnician(deleteDialog.technician.id);
        setDeleteDialog({ open: false, technician: null });
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, technician: null });
  };

  const handleSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <Box>
      <PageTitle
        title="Gestion des Techniciens"
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
            disabled={!hasPermission('add')}
            title={!hasPermission('add') ? 'Vous n\'avez pas la permission d\'ajouter des techniciens à cette équipe' : ''}
          >
            Ajouter un technicien
          </Button>
        }
      />

      <TechniciansList 
        key={refreshKey}
        onEdit={handleOpenForm}
        onDelete={handleDelete}
        hasPermission={hasPermission}
      />

      <Dialog 
        open={openForm} 
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedTechnician ? 'Modifier un technicien' : 'Ajouter un technicien'}
        </DialogTitle>
        <TechnicianForm
          technician={selectedTechnician}
          onClose={handleCloseForm}
          onSuccess={handleSuccess}
        />
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {deleteDialog.technician && (
              <>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <PersonIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 500 }}>
                    {deleteDialog.technician.firstName} {deleteDialog.technician.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {deleteDialog.technician.email}
                  </Typography>
                  {deleteDialog.technician.skills && deleteDialog.technician.skills.length > 0 && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }} useFlexGap>
                      {deleteDialog.technician.skills.map((skillId) => {
                        const skill = skills.find(s => s.id === skillId);
                        if (!skill) return null;
                        return (
                          <Chip
                            key={skill.id}
                            label={skill.name}
                            size="small"
                            sx={{
                              backgroundColor: `${skill.color}20`,
                              border: `1px solid ${skill.color}40`,
                              fontSize: '0.7rem',
                              height: '20px'
                            }}
                          />
                        );
                      })}
                    </Stack>
                  )}
                </Box>
              </>
            )}
          </Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cette action désactivera le technicien. Il ne sera plus disponible pour de nouvelles affectations mais ses astreintes existantes seront conservées.
          </Alert>
          <Typography variant="body1">
            Êtes-vous sûr de vouloir supprimer ce technicien ?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleDeleteCancel}>
            Annuler
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechniciansPage;
